/* eslint-disable no-nested-ternary */

import { FC, ReactNode, useRef, useState } from 'react';
import { Card, Form, Select, SelectProps, Space, Typography } from 'antd';
import type { FormItemProps } from 'antd/lib/form/FormItem';
import type { SizeType } from 'antd/lib/config-provider/SizeContext';
import { useDispatch, useSelector } from 'react-redux';
import { FaRegBuilding } from 'react-icons/fa';

const { Text } = Typography;

interface SelectSearchFrontendProps {
  name?: string;
  label?: string;
  placeholder?: string;
  searchKeys?: string[];
  rules?: FormItemProps['rules'];
  className?: string;
  size?: SizeType;
  optionRender?: (item: any) => ReactNode;
  optionValue?: string;
  optionLabel?: string | ((item: any) => string);
  allowClear?: boolean;
  autoFocus?: boolean;
  fetchOnMount?: boolean;
  bordered?: boolean;
  noPadding?: boolean;
  hidden?: boolean;
  multiple?: boolean;
  onChange?: SelectProps<any>['onChange'];
  storeKeySelector: string;
  storeAction: any;
  initialOption?: any | any[];
  filter?: (item: any) => boolean;
  formMode?: boolean;
  form?: any;
}

const SelectSearchFrontend: FC<SelectSearchFrontendProps> = ({
  name,
  label,
  placeholder,
  searchKeys = ['title'],
  rules,
  className,
  size,
  optionRender,
  optionValue = 'uuid',
  optionLabel = 'title',
  allowClear,
  autoFocus,
  fetchOnMount = true,
  bordered,
  noPadding = false,
  hidden,
  multiple,
  onChange,
  storeKeySelector,
  storeAction,
  initialOption,
  filter,
  formMode = false,
  form,
}) => {
  const fetchOnMountRef = useRef(true);
  const { loading, data } = useSelector((globalState: any) => globalState[storeKeySelector]);
  const dispatch = useDispatch();
  const items = data?.data;

  const selectRef = useRef<any>(null);
  const [searchText, setSearchText] = useState('');
  const [extraOptions, setExtraOptions] = useState<any[]>([]);

  if (fetchOnMount && fetchOnMountRef.current) {
    fetchOnMountRef.current = false;
    if (!loading) {
      dispatch(storeAction());
    }
  }

  const listInitialOptional = initialOption && !Array.isArray(initialOption) ? [initialOption] : initialOption;

  let options = [...extraOptions, ...(items ?? (initialOption ? listInitialOptional : []))];

  options = options?.filter((item: any) => {
    if (item.isCreate) return true;

    if (!searchText) return true;

    const label = typeof optionLabel === 'function' ? optionLabel(item) : item[optionLabel];

    return String(label).toLowerCase().includes(searchText.toLowerCase());
  });

  if (filter) {
    options = options.filter(filter);
  }

  return (
    <Form.Item className={className} label={label} name={name} rules={rules} noStyle={!label} hidden={hidden}>
      <Select
        ref={selectRef}
        dropdownClassName={noPadding ? 'no-padding' : ''}
        filterOption={false}
        loading={loading}
        placeholder={placeholder}
        className={`${!label ? className : undefined} plan-client-name`}
        size={size}
        allowClear={allowClear}
        autoFocus={autoFocus}
        mode={(formMode ? 'tags' : multiple ? 'multiple' : undefined) as any}
        showSearch
        bordered={bordered}
        value={
          formMode && Array.isArray(form?.getFieldValue(name)) ? form?.getFieldValue(name)?.slice(-1) : form?.getFieldValue(name)
        }
        onSearch={(value) => {
          if (!formMode) return;

          setSearchText(value);

          if (!value) {
            setExtraOptions([]);
            return;
          }

          const existsInItems = (items ?? []).some(
            (item: any) => String(item[optionValue]).toLowerCase() === value.toLowerCase(),
          );

          if (!existsInItems) {
            setExtraOptions([
              {
                isCreate: true,
                [optionValue]: value,
                [typeof optionLabel === 'string' ? optionLabel : 'label']: value,
              },
            ]);
          } else {
            setExtraOptions([]);
          }
        }}
        onChange={(value, option: any) => {
          if (formMode) {
            setExtraOptions([]);
            setSearchText('');

            selectRef.current?.blur();
          }

          const finalValue = Array.isArray(value) ? value.slice(-1) : value;

          if (formMode && form) {
            if (option) {
              const lastOption = Array.isArray(option) ? option[option.length - 1] : option;
              form.setFieldsValue({
                [name!]: finalValue,
                client_id: lastOption?.value,
                business_name: lastOption?.label,
              });
            } else {
              form.setFieldsValue({
                [name!]: undefined,
                client_id: undefined,
                business_name: undefined,
              });
            }
          }

          onChange?.(finalValue, option);
        }}
        optionLabelProp="display"
        options={options?.map((item: any) => {
          const { isCreate } = item;

          const display = typeof optionLabel === 'function' ? optionLabel(item) : item[optionLabel];

          return {
            key: isCreate ? `create-${display}` : item.uuid ?? item.value,
            value: item[optionValue] ?? item.value,
            label: isCreate ? (
              <Card bodyStyle={{ padding: '10px 20px', background: '#eff8fb40' }}>
                <Space size={20}>
                  <FaRegBuilding style={{ fontSize: '180%' }} />
                  <Space size={0} direction="vertical">
                    <Text strong>{display}</Text>
                    <Text className="primary">Add new</Text>
                  </Space>
                </Space>
              </Card>
            ) : optionRender ? (
              optionRender(item)
            ) : (
              display
            ),
            display,
            resource: item,
            isCreate,
          };
        })}
      />
    </Form.Item>
  );
};

export default SelectSearchFrontend;
